import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Edit, Trash2, Search, Home as HomeIcon } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import api, { resolveMedia } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';

export default function AdminProperties() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const load = () => {
    setLoading(true);
    api.get('/admin/properties').then(r => setItems(r.data || [])).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const remove = async (id) => {
    if (!window.confirm('Delete this property? This cannot be undone.')) return;
    await api.delete('/admin/properties/' + id);
    toast({ title: 'Deleted', description: 'Property removed.' });
    load();
  };

  const filtered = items.filter(p => !q || p.title.toLowerCase().includes(q.toLowerCase()) || (p.location || '').toLowerCase().includes(q.toLowerCase()));

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">Properties</h1>
          <p className="text-sm text-neutral-500">{items.length} total listings</p>
        </div>
        <Link to="/admin/properties/new"><Button className="btn-primary gap-2"><Plus className="w-4 h-4" /> New Property</Button></Link>
      </div>

      <div className="bg-white rounded-xl border border-neutral-200 p-4 mb-4">
        <div className="relative max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by title or location..." className="pl-9" />
        </div>
      </div>

      <div className="bg-white rounded-xl border border-neutral-200 overflow-hidden">
        {loading ? <div className="p-12 text-center text-neutral-500">Loading...</div> : filtered.length === 0 ? (
          <div className="p-12 text-center">
            <HomeIcon className="w-10 h-10 text-neutral-300 mx-auto mb-2" />
            <p className="text-neutral-500">No properties found</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 text-neutral-600">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Property</th>
                <th className="text-left px-4 py-3 font-semibold">Type</th>
                <th className="text-left px-4 py-3 font-semibold">Location</th>
                <th className="text-left px-4 py-3 font-semibold">Price</th>
                <th className="text-left px-4 py-3 font-semibold">Status</th>
                <th className="text-right px-4 py-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {filtered.map((p) => (
                <tr key={p.id} className="hover:bg-neutral-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <img src={resolveMedia(p.gallery?.[0]) || '/assets/logo.png'} alt={p.title} className="w-12 h-12 rounded object-cover bg-neutral-100" />
                      <div>
                        <p className="font-medium text-neutral-900">{p.title}</p>
                        <p className="text-xs text-neutral-500">{p.transaction_type} · {p.bedrooms || 0}BR · {p.floor_area || 0}sqm</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">{p.type}</td>
                  <td className="px-4 py-3">{p.location}</td>
                  <td className="px-4 py-3 font-semibold text-[#DC2626]">{p.price_label}</td>
                  <td className="px-4 py-3"><Badge variant="secondary">{p.status}</Badge> {p.featured && <Badge className="bg-[#DC2626] text-white ml-1">Featured</Badge>}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-1">
                      <Link to={'/admin/properties/' + p.id}><Button size="sm" variant="outline" className="gap-1"><Edit className="w-3.5 h-3.5" /> Edit</Button></Link>
                      <Button size="sm" variant="outline" onClick={() => remove(p.id)} className="gap-1 text-red-600 hover:text-red-700 border-red-200"><Trash2 className="w-3.5 h-3.5" /></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
